import React, { useEffect, useMemo, useState } from 'react';
import styles from './SuperCourseEdit.module.scss';
import Header from '../../../../components/header/Header';
import { useParams, useHistory } from 'react-router-dom';
import IconPlus from '../../../../assets/icons/plus_circle.svg?react';
import Spinner from '../../../../components/spinner/Spinner';
import { EditCourseProvider, useEditCourse } from './useEditCourse';
import EditProgramContent from './edit-program/EditProgramContent';
import EditLessonContent from './edit-lesson/EditLessonContent';
import EditContent from './edit-content/EditContent';
import { Unit, useGetAllUnitsAndSllLessonsQuery, useGetCourseQuery, useGetAusCourseQuery } from '@api/graphql-codegen';
import UnitList from './unitList/UnitList';
import { countryOptions } from '@src/constants';
import LessonList from './contentList/ContentList';
import { useQueryProgramWithCountry } from '@src/hooks';
import FooterPage from '@src/components/footer-page/FooterPage';

type Props = {};
type Params = {
  id: string;
}

function SuperCourseEdit(props: Props) {
  const {id} = useParams<Params>();
  const history = useHistory();
  const [primaryUnits, setPrimaryUnits] = useState<Unit[]>([])
  const [additionUnits, setAdditionUnits] = useState<Unit[]>([])
  const [primaryContents, setPrimaryContents] = useState<any[]>([])

  const {setProgram, setSelectionLevel, selectionLevel, setContent, setLesson, program} = useEditCourse();
  const {data: coursesOptions, loading: baseLoading} = useQueryProgramWithCountry();
  
  const isAusCourse = useMemo(() => coursesOptions.find(p => p.label === id)?.value === countryOptions[1].label, [coursesOptions, id])
  const isAmericanCourse = useMemo(() => coursesOptions.find(p => p.label === id)?.value.includes(countryOptions[0].label) || coursesOptions.find(p => p.label === id)?.value.includes(countryOptions[2].label), [coursesOptions, id])

  const {data: programData, loading: programDataLoading} = useGetCourseQuery({ variables: { input: { id } }, fetchPolicy: 'network-only', skip: !isAmericanCourse })
  const {data: ausProgramData, loading: ausProgramDataLoading} = useGetAusCourseQuery({ variables: { input: { id } }, fetchPolicy: 'network-only', skip: !isAusCourse })
  const {data: allUnitsAndLessons, loading: unitAndLessonLoading} = useGetAllUnitsAndSllLessonsQuery({fetchPolicy: 'cache-first', skip: !isAmericanCourse})

  // American Program Data
  useEffect(() => {
    if (programData && isAmericanCourse && allUnitsAndLessons) {
      const courseUnits = allUnitsAndLessons.getAllUnitsAndSLLLessons?.units ?? [];
      const updatedPrimaryUnits: Unit[] = [];
      // eslint-disable-next-line array-callback-return
      (programData?.getProgramUnits?.primary || []).map((unitId: string | null) => {
        if (unitId) {
          const pUnit = courseUnits.find(u => u?.id === unitId);
          if (pUnit) {
            updatedPrimaryUnits.push(pUnit);
          }
        }
      })
      const updatedAdditionUnits: Unit[] = [];
      // eslint-disable-next-line array-callback-return
      (programData?.getProgramUnits?.additional || []).map((unitId: string | null) => {
        if (unitId) {
          const aUnit = courseUnits.find(u => u?.id === unitId);
          if (aUnit) {
            updatedAdditionUnits.push(aUnit);
          }
        }
      })      

      setPrimaryUnits(updatedPrimaryUnits);
      setAdditionUnits(updatedAdditionUnits)
      setProgram({
        ...programData.getCourse as any,
        primary: updatedPrimaryUnits,
        additional: updatedAdditionUnits,
      })
      setSelectionLevel('Program');
      setContent(undefined);
      setLesson(undefined);
    }
  }, [allUnitsAndLessons, isAmericanCourse, programData, setContent, setLesson, setProgram, setSelectionLevel])

  // Australian Program Data
  useEffect(() => {
    if (ausProgramData && isAusCourse) {
      setProgram({ ...ausProgramData.getAUSCourse})
      setPrimaryContents(ausProgramData.getAUSCourse.courseContents ?? [])
      setSelectionLevel('Program');
      setContent(undefined);
      setLesson(undefined);
    }
  }, [ausProgramData, isAusCourse, setContent, setLesson, setProgram, setSelectionLevel])


  const handleAddUnits = () => {
    history.push({ pathname: '/super/courses/customize', state: { program } })
  }

  const content = useMemo(() => {
    switch (selectionLevel) {
      case 'Program': {
        return <EditProgramContent/>;
      }
      case 'Lesson': {
        return <EditLessonContent/>;
      }
      case 'Content': {
        return <EditContent/>;
      }
      default: {
        return null;
      }
    }
  }, [selectionLevel]);

  return (
    <>
      <Header />
      {(baseLoading || programDataLoading || ausProgramDataLoading || unitAndLessonLoading) ? <div className={styles.loading}><Spinner/></div> :
        <div className={styles.page}>
          <div className={styles.mainWrapper}>
            <div className={styles.list}>
              <div onClick={() => null} className={styles.programName}>
                <div className={styles.bolderPN}>Edit Course<br/><span
                  className={styles.ligtherPN}>{programData?.getCourse.name}</span></div>
              </div>
              {isAusCourse && <LessonList title='Primary' contents={primaryContents} unitOpened={true} courseId={id} isPrimary />}
              {isAmericanCourse && 
                <>
                  {primaryUnits?.length ? <UnitList title='Primary' units={primaryUnits} unitOpened={true} courseId={id} isPrimary /> : null}
                  {additionUnits?.length ? <UnitList title='Additional' units={additionUnits} unitOpened={true} courseId={id} /> : null}
                </>
              }
              {
                programData?.getCourse?.isParentCourse && programData?.getCourse?.isSpecial ?
                null :
                <div className={styles.addUnit}>
                  <IconPlus className={styles.icon}/>
                  <div className={styles.title} onClick={handleAddUnits}>{(primaryUnits?.length > 0 || additionUnits?.length > 0 || primaryContents.length > 0) ? isAusCourse ? 'Edit Content' : 'Edit Units' : isAusCourse ? 'Add Content' : 'Add Units'}</div>
                </div>
              }
            </div>
            <div className={styles.content}>
              {content}
            </div>
          </div>
          <FooterPage />
        </div>
      }
    </>
  );
}

const EditCourse = (props: Props) => {
  return (
    <EditCourseProvider>
      <SuperCourseEdit {...props}/>
    </EditCourseProvider>
  )
};

export default EditCourse;
